/**
 * Variant Block Refresh Manager
 * Handles dynamic refreshing of product blocks and sections when variants change
 */
class VariantBlockRefresh {
  constructor() {
    this.refreshQueue = new Set();
    this.isRefreshing = false;
    this.abortController = null;
    
    this.init();
  }

  init() {
    // Subscribe to the existing variant change event
    // This already provides us with sectionId, html, and variant
    this.unsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this)
    );

    // Don't listen to variant:changed - it causes duplicate firing
    // The pubsub event is sufficient and provides all the data we need
  }

  handleVariantChange({ data: { sectionId, html, variant } }) {
    this.refreshBlocksOrSection(sectionId, html, variant);
  }

  async fetchAndRefresh(sectionId, variant) {
    if (this.isRefreshing) {
      this.refreshQueue.add({ sectionId, variant });
      return;
    }

    this.isRefreshing = true;
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const productInfo = document.querySelector(`product-info[data-section="${sectionId}"]`);
      const url = productInfo?.dataset.url;
      if (!url) return;

      const response = await fetch(
        `${url}?variant=${variant.id}&section_id=${sectionId}`,
        { signal: this.abortController.signal }
      );
      
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      
      this.refreshBlocksOrSection(sectionId, html, variant);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching variant data:', error);
      }
    } finally {
      this.isRefreshing = false;
      this.processQueue();
    }
  }

  refreshBlocksOrSection(sectionId, html, variant) {
    const productInfo = document.querySelector(`product-info[data-section="${sectionId}"]`);
    
    // Check if entire product section should refresh
    if (productInfo?.dataset.refreshSectionOnVariant === 'true') {
      this.refreshEntireSection(productInfo, html, variant);
      return;
    }

    // Refresh individual blocks within the product section
    this.refreshBlocks(sectionId, html, variant);

    // Also check for external sections that should refresh on variant change
    this.refreshExternalSections(variant);
  }

  refreshExternalSections(variant) {
    // Find all external sections marked for refresh
    const externalSections = document.querySelectorAll('[data-refresh-section-on-variant="true"]');
    
    externalSections.forEach(async (section) => {
      const externalSectionId = section.dataset.id;
      if (!externalSectionId) return;

      // Fetch the updated section
      const productInfo = document.querySelector('product-info[data-update-url="true"]');
      if (!productInfo) return;

      const url = productInfo.dataset.url;
      
      try {
        const response = await fetch(
          `${url}?variant=${variant.id}&section_id=${externalSectionId}`
        );
        
        const text = await response.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        
        const updatedSection = tempDiv.querySelector(`[data-id="${externalSectionId}"]`);
        
        if (updatedSection) {
          this.addLoadingState(section);
          
          requestAnimationFrame(() => {
            section.innerHTML = updatedSection.innerHTML;
            section.className = updatedSection.className;
            
            // Reinitialize any components in the section
            this.reinitializeSection(section);
            
            this.removeLoadingState(section);
          });
        }
      } catch (error) {
        console.error('Error refreshing external section:', error);
      }
    });
  }

  reinitializeSection(section) {
    // Reinitialize tabs component if present
    const tabsComponent = section.querySelector('tabs-component');
    if (tabsComponent && typeof tabsComponent.connectedCallback === 'function') {
      tabsComponent.connectedCallback();
    }

    // Generic reinitialization for any custom elements
    const customElements = section.querySelectorAll('[is]');
    customElements.forEach(el => {
      if (typeof el.connectedCallback === 'function') {
        el.connectedCallback();
      }
    });
  }

  refreshEntireSection(productInfo, html, variant) {
    const sectionId = productInfo.dataset.section;
    const updatedProductInfo = html.querySelector(`product-info[data-section="${sectionId}"]`);
    
    if (!updatedProductInfo) {
      console.warn(`Could not find updated section with ID: ${sectionId}`);
      return;
    }

    // Add loading state to entire section
    this.addLoadingState(productInfo);

    requestAnimationFrame(() => {
      // Use the theme's existing HTMLUpdateUtility if available
      if (window.HTMLUpdateUtility) {
        HTMLUpdateUtility.viewTransition(
          productInfo,
          updatedProductInfo,
          [], // preProcessHtmlCallbacks
          [   // postProcessHtmlCallbacks
            (newNode) => {
              window?.Shopify?.PaymentButton?.init();
              window?.ProductModel?.loadShopifyXR();
            }
          ]
        );
      } else {
        // Fallback if HTMLUpdateUtility is not available
        productInfo.innerHTML = updatedProductInfo.innerHTML;
      }

      this.removeLoadingState(productInfo);

      // Dispatch event that section has been refreshed
      document.dispatchEvent(new CustomEvent('section:refreshed', {
        detail: { sectionId, variant }
      }));
    });
  }

  refreshBlocks(sectionId, html, variant) {
    // Find all blocks marked for refresh in the current section
    // Support both data-refresh-on-variant="true" and data-refresh-on-variant="blocktype"
    const blocksToRefresh = document.querySelectorAll(
      `[data-section-id="${sectionId}"][data-refresh-on-variant]`
    );

    if (blocksToRefresh.length === 0) return;

    blocksToRefresh.forEach(block => {
      this.refreshBlock(block, html, variant);
    });

    // Dispatch event that blocks have been refreshed
    document.dispatchEvent(new CustomEvent('blocks:refreshed', {
      detail: { sectionId, variant }
    }));
  }

  refreshBlock(blockElement, html, variant) {
    const blockId = blockElement.dataset.blockId;
    // Support both approaches: data-block-type or data-refresh-on-variant value
    const blockType = blockElement.dataset.blockType || blockElement.dataset.refreshOnVariant;

    // Add loading state
    this.addLoadingState(blockElement);

    // Find the corresponding block in the fetched HTML
    const updatedBlock = html.querySelector(`[data-block-id="${blockId}"]`);

    if (!updatedBlock) {
      console.warn(`Could not find updated block with ID: ${blockId}`);
      this.removeLoadingState(blockElement);
      return;
    }

    // Use requestAnimationFrame for smooth transition
    requestAnimationFrame(() => {
      // Replace the inner content
      blockElement.innerHTML = updatedBlock.innerHTML;
      
      // Copy over any updated classes (except loading)
      const newClasses = updatedBlock.className.split(' ').filter(c => !c.includes('loading'));
      blockElement.className = newClasses.join(' ');

      // Reinitialize any components within the block
      this.reinitializeBlock(blockElement, blockType);

      // Remove loading state
      this.removeLoadingState(blockElement);

      // Trigger a custom event for this specific block
      blockElement.dispatchEvent(new CustomEvent('block:refreshed', {
        detail: { blockType, variant },
        bubbles: true
      }));
    });
  }

  addLoadingState(element) {
    element.classList.add('block-refreshing');
    element.style.opacity = '0.6';
    element.style.pointerEvents = 'none';
  }

  removeLoadingState(element) {
    element.classList.remove('block-refreshing');
    element.style.opacity = '';
    element.style.pointerEvents = '';
  }

  reinitializeBlock(blockElement, blockType) {
    switch(blockType) {
      case 'inventory':
        // Reinitialize progress bar
        const progressBar = blockElement.querySelector('progress-bar');
        if (progressBar && typeof progressBar.connectedCallback === 'function') {
          progressBar.connectedCallback();
        }
        
        // Reinitialize any popup modals in inventory block
        const modal = blockElement.querySelector('basic-modal');
        if (modal && typeof modal.connectedCallback === 'function') {
          modal.connectedCallback();
        }
        break;

      case 'collapsible_tab':
        // Reinitialize accordion functionality
        const accordion = blockElement.querySelector('[data-accordion]');
        if (accordion) {
          // Re-attach event listeners or reinit component
          // This depends on how your accordion is initialized
        }
        break;

      case 'custom_liquid':
        // Execute any scripts in the custom liquid
        const scripts = blockElement.querySelectorAll('script');
        scripts.forEach(script => {
          const newScript = document.createElement('script');
          Array.from(script.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.textContent = script.textContent;
          script.parentNode.replaceChild(newScript, script);
        });
        break;

      case 'description':
        // No special reinitialization needed for description
        break;

      case 'complementary':
        // Reinitialize product cards, quick add buttons, etc.
        const quickAddButtons = blockElement.querySelectorAll('quick-add-button');
        quickAddButtons.forEach(btn => {
          if (typeof btn.connectedCallback === 'function') {
            btn.connectedCallback();
          }
        });
        break;

      default:
        // Generic reinitialization for custom elements
        const customElements = blockElement.querySelectorAll('[is]');
        customElements.forEach(el => {
          if (typeof el.connectedCallback === 'function') {
            el.connectedCallback();
          }
        });
    }
  }

  processQueue() {
    if (this.refreshQueue.size === 0) return;
    
    const next = Array.from(this.refreshQueue)[0];
    this.refreshQueue.delete(next);
    this.fetchAndRefresh(next.sectionId, next.variant);
  }

  destroy() {
    this.unsubscriber?.();
    this.abortController?.abort();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.variantBlockRefresh = new VariantBlockRefresh();
  });
} else {
  window.variantBlockRefresh = new VariantBlockRefresh();
}